import React, { useState, useEffect } from "react";
import {
  Container,
  Row,
  Col,
  Form,
  Button,
  Card,
  Alert,
  Spinner,
  Tabs,
  Tab,
} from "react-bootstrap";
import {
  FaUpload,
  FaLock,
  FaUser,
  FaPhone,
  FaBuilding,
  FaTrash,
  FaExternalLinkAlt,
  FaStar, 
  FaEye, 
  FaEyeSlash,
} from "react-icons/fa";
import Swal from "sweetalert2";
import { useTranslation } from "react-i18next";
import "bootstrap/dist/css/bootstrap.min.css";
import "../driverTable/PageOne/DarkTheme.css";
import NavbarMenu from "../heroSection/NavbarMenu";
import Footer from "../footer/Footer";
import { authAPI, driverAPI } from "../../api/apiSystem";
import ClientChatBox from "../chat/ClientChatBox";
import { Link } from "react-router-dom";

export default function DriverInfoForm() {
  const { t } = useTranslation();

  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  // 🔹 SweetAlert Configuration
  const swalConfig = {
    confirmButtonColor: "#ff6600",
    cancelButtonColor: "#6c757d",
    timer: 4000,
  };

  // 🔹 SweetAlert Helpers
  const showAlert = {
    // Loading alert
    loading: (title) => {
      Swal.fire({
        title,
        allowOutsideClick: false,
        didOpen: () => Swal.showLoading(),
        ...swalConfig,
      });
    },

    // Success alert
    success: (title, text = "") => {
      Swal.fire({
        icon: "success",
        title,
        text,
        ...swalConfig,
        timer: 3000,
      });
    },

    // Error alert
    error: (title, text = "") => {
      Swal.fire({
        icon: "error",
        title,
        text,
        ...swalConfig,
      });
    },

    // Warning alert with confirmation
    warning: (title, text, confirmText, cancelText) => {
      return Swal.fire({
        icon: "warning",
        title,
        text,
        showCancelButton: true,
        confirmButtonColor: swalConfig.confirmButtonColor,
        cancelButtonColor: swalConfig.cancelButtonColor,
        confirmButtonText: confirmText,
        cancelButtonText: cancelText,
        reverseButtons: true,
      });
    },

    // Validation error alert with list
    validationError: (title, errors) => {
      const html = Array.isArray(errors)
        ? errors.map((error) => `• ${error}`).join("<br>")
        : `• ${errors}`;

      Swal.fire({
        icon: "error",
        title,
        html,
        ...swalConfig,
        timer: 5000,
      });
    },

    // Close any open alert
    close: () => Swal.close(),
  };

  // States
  const [formData, setFormData] = useState({
    title: "Mr",
    firstname: "",
    company_name: "",
    lastname: "",
    nickname: "",
    email: "",
    street_address: "",
    addressone: "",
    postal_code: "",
    telphone: "",
    website: "",
    tax_number: "",
    local_court: "",
    responsible_person: "",
    average_rating: 0,
    total_reviews: 0,
    email_checked: true,
    sms_checked: true,
    is_term: false,
    is_data_protection: false,
    is_transport_conditions: false,
    is_legal_notice: false,
  });

  const [passwordData, setPasswordData] = useState({
    old_password: "",
    new_password: "",
    new_password_confirmation: "",
  });

  const [deleteData, setDeleteData] = useState({
    password: "",
    deleted_reason: "",
  });

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");

  const [passwordLoading, setPasswordLoading] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [passwordErrors, setPasswordErrors] = useState({});
  const [deleteErrors, setDeleteErrors] = useState({});

  const [files, setFiles] = useState({
    comercial_image: [],
    carrierinsurance_image: [],
    efu_license_image: [],
  });

  const [existingFiles, setExistingFiles] = useState({
    comercial_image: null,
    carrierinsurance_image: null,
    efu_license_image: null,
  });

  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const [showPasswords, setShowPasswords] = useState({
    old_password: false,
    new_password: false,
    new_password_confirmation: false,
  });

  const togglePassword = (field) => {
    setShowPasswords((prev) => ({
      ...prev,
      [field]: !prev[field],
    }));
  };

  const getPasswordStrength = (password) => {
    if (!password) return { level: 0, label: "", color: "#e5e7eb" };
    let score = 0;
    if (password.length >= 8) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;

    if (score <= 2)
      return { level: 1, label: t("common.weak", "Weak"), color: "#dc2626" };
    if (score === 3)
      return { level: 2, label: t("common.medium", "Medium"), color: "#d97706" };
    if (score === 4)
      return { level: 3, label: t("common.strong", "Strong"), color: "#16a34a" };
    return { level: 4, label: t("common.veryStrong", "Very strong"), color: "#059669" };
  };

  useEffect(() => {
    fetchDriverData();
  }, []);

  const fetchDriverData = async () => {
    try {
      setLoading(true);

      const profileResponse = await authAPI.getProfile();
      console.log("Full API response:", profileResponse);

      if (profileResponse.success && profileResponse.data) {
        const profile = profileResponse.data.data;
        console.log("Profile data for form:", profile);

        const getValue = (value, defaultValue = "") => {
          return value !== null && value !== undefined ? value : defaultValue;
        };

        setFormData({
          title: getValue(profile.title, "Mr"),
          photo: getValue(profile.photo),
          firstname: getValue(profile.firstname),
          company_name: getValue(profile.company_name),
          lastname: getValue(profile.lastname),
          nickname: getValue(profile.nicname),
          email: getValue(profile.email),
          street_address: getValue(profile.street_address),
          addressone: getValue(profile.addressone),
          postal_code: getValue(profile.postal_code),
          telphone: getValue(profile.telphone),
          website: getValue(profile.website),
          tax_number: getValue(profile.tax_number),
          local_court: getValue(profile.local_court),
          responsible_person: getValue(profile.responsible_person),
          average_rating: getValue(profile.average_rating),
          total_reviews: getValue(profile.total_reviews),
          email_checked: Boolean(profile.email_checked),
          sms_checked: Boolean(profile.sms_checked),
          is_term: Boolean(profile.is_term),
          is_data_protection: Boolean(profile.is_data_protection),
          is_transport_conditions: Boolean(profile.is_transport_conditions),
          is_legal_notice: Boolean(profile.is_legal_notice),
        });

        if (profile.photo) {
          setPhotoPreview(profile.photo);
        }

        // Save existing file information
        setExistingFiles({
          comercial_image: profile.comercial_image,
          carrierinsurance_image: profile.carrierinsurance_image,
          efu_license_image: profile.efu_license_image,
        });
      } else {
        showAlert.error(t("alerts.error"), t("errors.loadProfile"));
      }

      // Load vehicles data
      try {
        const vehiclesResponse = await driverAPI.vehicles.index();
        if (vehiclesResponse.success) {
          setVehicles(vehiclesResponse.data || []);
        }
      } catch (vehicleError) {
        console.error("Error fetching vehicles:", vehicleError);
        setVehicles([]);
      }
    } catch (err) {
      console.error("Error fetching driver data:", err);
      showAlert.error(t("alerts.error"), t("errors.loadDriverData"));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  // const handleFileChange = (field, fileList) => {
  //   setFiles((prev) => ({
  //     ...prev,
  //     [field]: Array.from(fileList || []),
  //   }));
  // };
  const handleFileChange = (e, setFile, setPreview) => {
    const file = e.target.files[0];
    if (file) {
      setFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };
  const getFileName = (url) => {
    if (!url || typeof url !== "string") {
      return t("files.uploadedFile");
    }

    return decodeURIComponent(url.split("/").pop());
  };

  const getExistingFileList = (fileInfo) => {
    if (!fileInfo) {
      return [];
    }

    if (Array.isArray(fileInfo.files) && fileInfo.files.length > 0) {
      return fileInfo.files.map((file) => ({
        ...file,
        name: file.name || getFileName(file.url),
      }));
    }

    const url = fileInfo.url || fileInfo;

    return url
      ? [
          {
            id: fileInfo.id || null,
            url,
            name: getFileName(url),
          },
        ]
      : [];
  };

  const handleDeleteDocumentFile = async (fileId) => {
    if (!fileId) {
      return;
    }

    const result = await Swal.fire({
      icon: "warning",
      title: t("common.delete"),
      text: t("files.deleteConfirm", "Diese Datei wirklich löschen?"),
      showCancelButton: true,
      confirmButtonColor: "#dc3545",
      cancelButtonColor: "#6c757d",
      confirmButtonText: t("common.delete"),
      cancelButtonText: t("common.cancel"),
    });

    if (!result.isConfirmed) {
      return;
    }

    const response = await driverAPI.deleteDocumentFile(fileId);

    if (response.success) {
      showAlert.success(t("alerts.success"), t("files.deleteSuccess", "Datei gelöscht"));
      await fetchDriverData();
      return;
    }

    showAlert.error(
      t("alerts.error"),
      response.message || t("files.deleteFailed", "Datei konnte nicht gelöscht werden"),
    );
  };

  const validateForm = () => {
    const errors = [];

    // Email validation
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.push(t("validation.invalidEmail"));
    }

    // Required fields validation
    if (!formData.firstname.trim()) {
      errors.push(t("validation.firstNameRequired"));
    }

    if (!formData.lastname.trim()) {
      errors.push(t("validation.lastNameRequired"));
    }

    // Terms and conditions validation
    if (!formData.is_term) {
      errors.push(t("validation.termsRequired"));
    }

    if (!formData.is_data_protection) {
      errors.push(t("validation.termsRequired"));
    }

    if (!formData.is_transport_conditions) {
      errors.push(t("validation.termsRequired"));
    }

    if (!formData.is_legal_notice) {
      errors.push(t("validation.termsRequired"));
    }

    // File validation
    const validFileTypes = ["pdf", "jpg", "png", "jpeg"];
    Object.entries(files).forEach(([field, selectedFiles]) => {
      selectedFiles.forEach((file) => {
        const fileExtension = file.name.split(".").pop().toLowerCase();
        if (!validFileTypes.includes(fileExtension)) {
          errors.push(
            t("validation.invalidFileType", { field: t(`files.${field}`) }),
          );
        }

        // File size validation (max 5MB)
        if (file.size > 5 * 1024 * 1024) {
          errors.push(
            t("validation.fileSizeExceeded", { field: t(`files.${field}`) }),
          );
        }
      });
    });

    return errors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validateForm();
    if (validationErrors.length > 0) {
      showAlert.validationError(t("alerts.validationError"), validationErrors);
      return;
    }

    setSubmitting(true);
    showAlert.loading(t("common.saving"));

    try {
      const submitData = new FormData();

      // Add text data - use nicname instead of nickname
      const apiFormData = {
        ...formData,
        nicname: formData.nickname, // Convert nickname to nicname for API
      };

      delete apiFormData.nickname; // Remove unwanted field

      Object.keys(apiFormData).forEach((key) => {
        let value = apiFormData[key];

        // Convert boolean values to 1 or 0
        if (typeof value === "boolean") {
          value = value ? 1 : 0;
        }

        // Convert empty values to empty strings
        if (value === null || value === undefined) {
          value = "";
        }

        submitData.append(key, value);
      });

      // Add files
      Object.entries(files).forEach(([field, selectedFiles]) => {
        selectedFiles.forEach((file) => {
          submitData.append(`${field}[]`, file);
        });
      });

      console.log("Submitting form data...", {
        is_data_protection: formData.is_data_protection,
        is_data_protection_api_value: formData.is_data_protection ? 1 : 0,
        is_transport_conditions: formData.is_transport_conditions,
        is_transport_conditions_api_value: formData.is_transport_conditions ? 1 : 0,
        is_legal_notice: formData.is_legal_notice,
        is_legal_notice_api_value: formData.is_legal_notice ? 1 : 0,
      });

      if (photoFile) {
        submitData.append("photo", photoFile);
      }

      const updateResponse = await driverAPI.updateProfile(submitData);

      showAlert.close();

      if (updateResponse.success) {
        showAlert.success(t("alerts.success"), t("alerts.profileUpdated"));

        if (updateResponse.data?.data?.photo) {
          setPhotoPreview(updateResponse.data.data.photo);
          setFormData((prev) => ({
            ...prev,
            photo: updateResponse.data.data.photo,
          }));
        }

        // Reset files after successful save
        setFiles({
          comercial_image: [],
          carrierinsurance_image: [],
          efu_license_image: [],
        });

        setPhotoFile(null);
        // Refresh data
        await fetchDriverData();
      } else {
        showAlert.error(
          t("alerts.error"),
          updateResponse.message || t("errors.updateProfile"),
        );
      }
    } catch (err) {
      console.error("Update error:", err);
      showAlert.close();
      showAlert.error(t("alerts.error"), t("errors.updateError"));
    } finally {
      setSubmitting(false);
    }
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData((prev) => ({ ...prev, [name]: value }));
    if (passwordErrors[name]) {
      setPasswordErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const validatePasswordForm = () => {
    const errors = {};
    if (!passwordData.old_password.trim())
      errors.old_password = t("validation.oldPasswordRequired");
    if (!passwordData.new_password.trim())
      errors.new_password = t("validation.newPasswordRequired");
    else if (passwordData.new_password.length < 6)
      errors.new_password = t("validation.passwordMinLength");
    if (!passwordData.new_password_confirmation.trim())
      errors.new_password_confirmation = t(
        "validation.confirmPasswordRequired",
      );
    else if (
      passwordData.new_password !== passwordData.new_password_confirmation
    )
      errors.new_password_confirmation = t("validation.passwordsDoNotMatch");
    return errors;
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validatePasswordForm();
    if (Object.keys(validationErrors).length > 0) {
      setPasswordErrors(validationErrors);

      // Show validation errors in SweetAlert
      const errorMessages = Object.values(validationErrors);
      showAlert.validationError(t("alerts.validationError"), errorMessages);
      return;
    }

    setPasswordLoading(true);
    setPasswordErrors({});

    showAlert.loading(t("common.changing"));

    try {
      const response = await authAPI.changePassword(passwordData);

      showAlert.close();

      if (response.success) {
        showAlert.success(t("alerts.success"), t("alerts.passwordChanged"));
        setPasswordData({
          old_password: "",
          new_password: "",
          new_password_confirmation: "",
        });
      } else {
        showAlert.error(
          t("alerts.error"),
          response.message || t("errors.changePassword"),
        );
      }
    } catch (error) {
      showAlert.close();
      showAlert.error(t("alerts.error"), t("errors.changePasswordError"));
    } finally {
      setPasswordLoading(false);
    }
  };

  const handleDeleteChange = (e) => {
    const { name, value } = e.target;
    setDeleteData((prev) => ({ ...prev, [name]: value }));
    if (deleteErrors[name]) {
      setDeleteErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleDeleteAccount = async (e) => {
    e.preventDefault();

    // Validate password
    if (!deleteData.password.trim()) {
      setDeleteErrors({ password: t("validation.passwordRequiredForDelete") });
      showAlert.error(
        t("alerts.error"),
        t("validation.passwordRequiredForDelete"),
      );
      return;
    }

    // Show confirmation dialog
    const result = await showAlert.warning(
      t("driverInfo.deleteAccountConfirmTitle"),
      t("driverInfo.deleteAccountConfirmText"),
      t("actions.yesDelete"),
      t("actions.cancel"),
    );

    if (!result.isConfirmed) {
      return;
    }

    setDeleteLoading(true);
    setDeleteErrors({});

    try {
      const response = await authAPI.deleteAccount(deleteData);
      if (response.success) {
        Swal.fire({
          icon: "success",
          title: t("alerts.success"),
          html: t("alerts.accountDeletedSuccessfully"),
          confirmButtonColor: "#ff6600",
        }).then(() => {
          // Logout and redirect
          localStorage.removeItem("token");
          localStorage.removeItem("temp_token");
          window.location.href = "/";
        });
      } else {
        showAlert.error(
          t("alerts.error"),
          response.message || t("errors.deleteAccountError"),
        );
      }
    } catch (error) {
      showAlert.error(
        t("alerts.error"),
        error.message || t("errors.deleteAccountError"),
      );
    } finally {
      setDeleteLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 4) return "#0d9f6e";      // أخضر
    if (score >= 3) return "#d97706";      // برتقالي
    return "#dc2626";                       // أحمر
  };

  if (loading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}
      >
        <Spinner animation="border" variant="warning" />
        <span className="ms-2">{t("common.loading")}</span>
      </div>
    );
  }

  const overall = formData.average_rating ?? formData.rating ?? 0;
  const totalReviews = formData.total_reviews ?? 0;
  const overallColor = getScoreColor(overall);

  console.log("👨‍💼 Driver info data:", formData, overall, overallColor);
  return (
    <>
      <NavbarMenu />
      <Container
        fluid
        className="client-info-section py-5 d-flex justify-content-center"
      >
        <Container
          className="card"
          style={{
            borderRadius: "12px",
            padding: "25px",
          }}
        >
          {/* 🔹 Tabs Navigation */}
          <Tabs
            defaultActiveKey="info"
            id="client-info-tabs"
            className="custom-tabs mb-4 fw-bold justify-content-center"
            fill
          >
            {/* 🧩 Tab 1: Driver Information */}
            <Tab
              eventKey="info"
              title={
                <span className="tab-title">
                  <FaUser className="me-2" />
                  {t("driverInfo.tabInformation")}
                </span>
              }
            >
              <Row>
                <Col md={7}>
                  <h5 className="fw-bold mb-4" style={{ color: "#ff7b00" }}>
                    {t("driverInfo.userData")}
                  </h5>

                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div className="d-flex align-items-center gap-2">
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center"
                        style={{
                          width: 36,
                          height: 36,
                          background: overallColor,
                          color: "#fff",
                        }}
                      >
                        <FaStar size={16} />
                      </div>
                      <div>
                        <div className="fw-bold" style={{ fontSize: 14, color: "#101828" }}>
                          {t("rating.criteria.rating.title", "Kundenbewertung")}
                        </div>
                        {/* <div className="text-muted" style={{ fontSize: 11 }}>
                          {t("rating.display_subtitle", "Bewertung dieses Auftrags")}
                        </div> */}
                      </div>
                    </div>
            
                    <div className="d-flex gap-2">
                      {/* Overall score badge */}
                      <div
                        className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                        style={{ background: overallColor, color: "#fff" }}
                      >
                        <FaStar size={12} />
                        <span className="fw-bold" style={{ fontSize: 14 }}>
                          {overall.toFixed(1)}
                        </span>
                      </div>

                      {/* Total Reviews badge */}
                      <div
                        className="d-flex align-items-center gap-2 px-3 py-1 rounded-pill"
                        style={{ background: '#033b49', color: "#fff" }}
                      >
                        <span className="fw-bold" style={{ fontSize: 14, color: "#fff" }}>
                          {totalReviews}
                        </span>
                      </div>
                    </div>
                  </div>

                  <Form onSubmit={handleSubmit}>
                    {/* Basic Information */}
                    <div className="mb-4">
                      <h5 className="d-flex align-items-center mb-3 dark-text-white">
                        <FaUser
                          className="me-2"
                          style={{ color: "#ff7b00" }}
                          size={22}
                        />
                        {t("driverInfo.basicInformation")}
                      </h5>

                      {/* Salutation - linked to title field */}
                      <Col xs={12} md={6} className="mx-auto my-0">
                        <div className="text-center mb-3">
                          <p className="fw-bold">
                            {photoPreview || formData.photo
                              ? t("existing_photo", "Aktuelles Foto")
                              : t("no_photo", "Kein Foto")}
                          </p>
  
                          <div
                            className="rounded-circle d-inline-flex align-items-center justify-content-center overflow-hidden mx-auto"
                            style={{
                              width: 130,
                              height: 130,
                              background: photoPreview ? "transparent" : "#f3f4f6",
                              border: "3px solid #ff7b00",
                            }}
                          >
                            {photoPreview ? (
                              <img
                                src={photoPreview}
                                alt="Profile"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                                onError={(e) => {
                                  e.target.style.display = "none";
                                }}
                              />
                            ) : (
                              <FaUser size={50} style={{ color: "#9ca3af" }} />
                            )}
                          </div>
                        </div>
  
                        <Form.Group controlId="formPhotoFile" className="mb-3 text-center">
                          <Form.Label className="fw-bold">{t("new_photo", "Neues Foto")}</Form.Label>
                          <Form.Control
                            type="file"
                            accept="image/*"
                            onChange={(e) => handleFileChange(e, setPhotoFile, setPhotoPreview)}
                          />
                          {photoFile && (
                            <Form.Text className="text-muted d-block mt-1">
                              {photoFile.name} ({(photoFile.size / 1024).toFixed(1)} KB)
                            </Form.Text>
                          )}
                        </Form.Group>
                      </Col>

                      <Form.Group className="mb-3">
                        <Form.Label className="fw-bold dark-text-white">
                          {t("driverInfo.salutation")}
                        </Form.Label>
                        <div className="d-flex gap-3">
                          <Form.Check
                            type="radio"
                            label={
                              <span className="">
                                {t("driverInfo.mr")}
                              </span>
                            }
                            name="title"
                            value="Mr"
                            checked={formData.title === "Mr"}
                            onChange={handleInputChange}
                            className="dark-text-white"
                          />
                          <Form.Check
                            type="radio"
                            label={
                              <span className="">
                                {t("driverInfo.ms")}
                              </span>
                            }
                            name="title"
                            value="Ms"
                            checked={formData.title === "Ms"}
                            onChange={handleInputChange}
                            className="dark-text-white"
                          />
                        </div>
                      </Form.Group>

                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.companyName")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t(
                                "driverInfo.companyNamePlaceholder",
                              )}
                              name="company_name"
                              value={formData.company_name}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.firstName")} *
                            </Form.Label>
                            <Form.Control
                              placeholder={t("driverInfo.firstNamePlaceholder")}
                              name="firstname"
                              value={formData.firstname}
                              onChange={handleInputChange}
                              required
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.lastName")} *
                            </Form.Label>
                            <Form.Control
                              placeholder={t("driverInfo.lastNamePlaceholder")}
                              name="lastname"
                              value={formData.lastname}
                              onChange={handleInputChange}
                              required
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.nickname")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t("driverInfo.nicknamePlaceholder")}
                              name="nickname"
                              value={formData.nickname}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.streetAddress")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t(
                                "driverInfo.streetAddressPlaceholder",
                              )}
                              name="street_address"
                              value={formData.street_address}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.additionalAddress")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t(
                                "driverInfo.additionalAddressPlaceholder",
                              )}
                              name="addressone"
                              value={formData.addressone}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.postalCodeCity")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t(
                                "driverInfo.postalCodeCityPlaceholder",
                              )}
                              name="postal_code"
                              value={formData.postal_code}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* Contact Details */}
                    <div className="mb-4">
                      <h5 className="d-flex align-items-center mb-3 dark-text-white">
                        <FaPhone
                          className="me-2"
                          style={{ color: "#ff7b00" }}
                          size={22}
                        />
                        {t("driverInfo.contactDetails")}
                      </h5>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.phone")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t("driverInfo.phonePlaceholder")}
                              name="telphone"
                              value={formData.telphone}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.email")}
                            </Form.Label>
                            <Form.Control
                              type="email"
                              placeholder={t("driverInfo.emailPlaceholder")}
                              name="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.website")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t("driverInfo.websitePlaceholder")}
                              name="website"
                              value={formData.website}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* Commercial Information */}
                    <div className="mb-4">
                      <h5 className="d-flex align-items-center mb-3 dark-text-white">
                        <FaBuilding
                          className="me-2"
                          style={{ color: "#ff7b00" }}
                          size={22}
                        />
                        {t("driverInfo.companyData")}
                      </h5>
                      <Row className="g-3">
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.taxNumber")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t("driverInfo.taxNumberPlaceholder")}
                              name="tax_number"
                              value={formData.tax_number}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.localCourt")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t(
                                "driverInfo.localCourtPlaceholder",
                              )}
                              name="local_court"
                              value={formData.local_court}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                        <Col md={6}>
                          <Form.Group>
                            <Form.Label className="fw-bold dark-text-white">
                              {t("driverInfo.responsiblePerson")}
                            </Form.Label>
                            <Form.Control
                              placeholder={t(
                                "driverInfo.responsiblePersonPlaceholder",
                              )}
                              name="responsible_person"
                              value={formData.responsible_person}
                              onChange={handleInputChange}
                              className="dark-bg-dark dark-text-white border-secondary"
                            />
                          </Form.Group>
                        </Col>
                      </Row>
                    </div>

                    {/* Notifications */}
                    <div className="mb-4">
                      <h6 className="fw-bold mb-3 dark-text-white">
                        {t("driverInfo.priceRequests")}
                      </h6>
                      <div className="d-flex flex-column gap-2">
                        <Form.Check
                          type="checkbox"
                          label={
                            <span className="">
                              {t("driverInfo.email")}
                            </span>
                          }
                          name="email_checked"
                          checked={formData.email_checked}
                          onChange={handleInputChange}
                          className="dark-text-white"
                        />
                        <Form.Check
                          type="checkbox"
                          label={
                            <span className="">
                              {t("driverInfo.sms")}
                            </span>
                          }
                          name="sms_checked"
                          checked={formData.sms_checked}
                          onChange={handleInputChange}
                          className="dark-text-white"
                        />
                      </div>
                    </div>

                    {/* Terms and Conditions */}
                    <Form.Group className="mt-4">
                      <Form.Check
                        type="checkbox"
                        name="is_term"
                        checked={formData.is_term}
                        onChange={handleInputChange}
                        className="dark-text-white"
                        label={
                          <>
                            <Link
                              to="/driver-terms"
                              // target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "var(--text-blue)", textDecoration: "auto" }}
                              onClick={(e) => e.stopPropagation()}
                              className="me-1"
                              >
                              {t("driverInfo.agreeToTerms")}{" "}

                              <b className="text-orange me-1">
                                {t("driverInfo.termsAndConditions")}
                              </b>

                              {t("driverInfo.termsAndConditions2")} 

                              <FaExternalLinkAlt className="text-orange mx-2" />
                            </Link>
                          </>
                        }
                        required
                      />
                    </Form.Group>

                    {/* Data Protections */}
                    <Form.Group className="mt-2">
                      <Form.Check
                        type="checkbox"
                        name="is_data_protection"
                        checked={formData.is_data_protection}
                        onChange={handleInputChange}
                        className="dark-text-white"
                        label={
                          <>
                            <Link
                              to="/data-protections"
                              // target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "var(--text-blue)", textDecoration: "auto" }}
                              onClick={(e) => e.stopPropagation()}
                              className="me-1"
                              >
                              {t("driverInfo.agreeToTerms")}{" "}

                              <b className="text-orange me-1">
                                {t("driverInfo.DataAndProtections")}
                                <FaExternalLinkAlt className="text-orange mx-2" />
                              </b>

                              {t("driverInfo.termsAndConditions2")} 

                            </Link>
                          </>
                        }
                        required
                      />
                    </Form.Group>

                    {/* Transport Conditions */}
                    <Form.Group className="mt-2">
                      <Form.Check
                        type="checkbox"
                        name="is_transport_conditions"
                        checked={formData.is_transport_conditions}
                        onChange={handleInputChange}
                        className="dark-text-white"
                        label={
                        <>
                            <Link
                              to="/transport-conditions"
                              // target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "var(--text-blue)", textDecoration: "auto" }}
                              onClick={(e) => e.stopPropagation()}
                              className="me-1"
                              >
                              {t("driverInfo.agreeToTerms")}{" "}

                              <b className="text-orange me-1">
                                {t("driverInfo.transportAndConditions")}
                                <FaExternalLinkAlt className="text-orange mx-2" />
                              </b>

                              {t("driverInfo.termsAndConditions2")} 

                            </Link>
                          </>
                        }
                        required
                      />
                    </Form.Group>

                    {/* Legal Notice */}
                    <Form.Group className="mt-2">
                      <Form.Check
                        type="checkbox"
                        name="is_legal_notice"
                        checked={formData.is_legal_notice}
                        onChange={handleInputChange}
                        className="dark-text-white"
                        label={
                          <>
                            <Link
                              to="/legal-notice"
                              // target="_blank"
                              rel="noopener noreferrer"
                              style={{ color: "var(--text-blue)", textDecoration: "auto" }}
                              onClick={(e) => e.stopPropagation()}
                              className="me-1"
                              >
                              {t("driverInfo.agreeToTerms")}{" "}

                              <b className="text-orange me-1">
                                {t("driverInfo.LegalAndNotice")}
                              </b>

                              {t("driverInfo.termsAndConditions3")} 

                              <FaExternalLinkAlt className="text-orange mx-2" />
                            </Link>
                          </>
                        }
                        required
                      />
                    </Form.Group>

                    {/* Save Button */}
                    <div className="text-center mt-4">
                      <Button
                        type="submit"
                        disabled={submitting}
                        style={{
                          backgroundColor: "#ff7b00",
                          border: "none",
                          padding: "10px 40px",
                          fontSize: "16px",
                          fontWeight: "bold",
                          borderRadius: "25px",
                          marginRight: "10px",
                        }}
                      >
                        {submitting ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              role="status"
                              aria-hidden="true"
                              className="me-2"
                            />
                            {t("common.saving")}
                          </>
                        ) : (
                          t("actions.save")
                        )}
                      </Button>
                    </div>
                  </Form>

                  {vehicles.length > 0 && (
                    <div className="mt-5">
                      <h6 className="fw-bold" style={{ color: "#ff7b00" }}>
                        {t("driverInfo.yourVehicles")}
                      </h6>
                      {vehicles.map((vehicle, index) => (
                        <Card
                          key={index}
                          className="mb-2 dark-section"
                          style={{
                            background:
                              "linear-gradient(145deg, #2a2a2a 0%, #1e1e1e 50%, #252525 100%)",
                            border: "1px solid #3a3a3a",
                          }}
                        >
                          <Card.Body>
                            <p className="mb-1 dark-text-white">
                              <strong style={{ color: "#ff7b00" }}>
                                {t("vehicles.licenseNumber")}:
                              </strong>{" "}
                              {vehicle.vehicle_number || vehicle.license_plate}
                            </p>
                            <p className="mb-1 dark-text-white">
                              <strong style={{ color: "#ff7b00" }}>
                                {t("vehicles.vehicleType")}:
                              </strong>{" "}
                              {vehicle.vehicle_type || vehicle.type}
                            </p>
                            <p className="mb-0 dark-text-white">
                              <strong style={{ color: "#ff7b00" }}>
                                {t("vehicles.maxWeight")}:
                              </strong>{" "}
                              {vehicle.max_weight || vehicle.weight} kg
                            </p>
                          </Card.Body>
                        </Card>
                      ))}
                    </div>
                  )}
                </Col>

                {/* ➡️ Right side - file upload cards */}
                <Col md={5} className="mt-4 mt-md-0">
                  {[
                    {
                      title: t("files.commercialRegistration.title"),
                      desc: t("files.commercialRegistration.description"),
                      field: "comercial_image",
                      existingFiles: getExistingFileList(existingFiles.comercial_image),
                    },
                    {
                      title: t("files.carrierInsurance.title"),
                      desc: t("files.carrierInsurance.description"),
                      field: "carrierinsurance_image",
                      existingFiles: getExistingFileList(existingFiles.carrierinsurance_image),
                    },
                    {
                      title: t("files.euLicense.title"),
                      desc: t("files.euLicense.description"),
                      field: "efu_license_image",
                      existingFiles: getExistingFileList(existingFiles.efu_license_image),
                    },
                  ].map((card, idx) => (
                    <Card
                      key={idx}
                      className="mb-4 p-3 dark-section"
                      style={{
                        background:
                          "var(--dark-border)",
                        borderRadius: "12px",
                        boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                        border: "1px solid #3a3a3a",
                      }}
                    >
                      <h6 style={{ color: "#ff7b00" }}>{card.title}</h6>
                      <p className="small dark-text-muted">{card.desc}</p>

                      {card.existingFiles.length > 0 && (
                        <div
                          className="mb-2 p-2 rounded d-flex flex-column gap-2"
                          style={{ background: "var(--dark-bg-primary)" }}
                        >
                          <small style={{ color: "#28a745" }}>
                            {t("files.currentFile")}:
                          </small>
                          {card.existingFiles.map((file) => (
                            <div
                              key={file.id || file.url}
                              className="d-flex align-items-center justify-content-between gap-2"
                            >
                              <a
                                href={file.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="small text-truncate"
                                style={{ color: "#ff7b00" }}
                              >
                                {file.name || getFileName(file.url)}
                              </a>
                              {file.id && (
                                <Button
                                  type="button"
                                  size="sm"
                                  variant="outline-danger"
                                  onClick={() => handleDeleteDocumentFile(file.id)}
                                  className="d-flex align-items-center gap-1"
                                >
                                  <FaTrash size={12} />
                                  {t("common.delete")}
                                </Button>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      <div
                        className="border rounded d-flex flex-column align-items-center justify-content-center py-4"
                        style={{
                          borderColor: "#ff7b00",
                        }}
                      >
                        <FaUpload
                          size={30}
                          className="mb-2"
                          style={{ color: "#ff7b00" }}
                        />
                        <span className="small dark-text-muted mb-2">
                          {t("files.dropFilesHere")}
                        </span>
                        <input
                          type="file"
                          id={`file-${idx}`}
                          style={{ display: "none" }}
                          onChange={(e) =>
                            handleFileChange(card.field, e.target.files)
                          }
                          multiple
                          accept=".pdf,.jpg,.png,.jpeg"
                        />
                        <Button
                          as="label"
                          htmlFor={`file-${idx}`}
                          size="sm"
                          style={{
                            backgroundColor: "#ff7b00",
                            border: "none",
                            borderRadius: "20px",
                            padding: "5px 15px",
                            cursor: "pointer",
                          }}
                        >
                          {t("files.chooseFiles")}
                        </Button>
                        {files[card.field].length > 0 && (
                          <div className="small mt-2" style={{ color: "#28a745" }}>
                            <div>{t("files.selected")}:</div>
                            {files[card.field].map((file) => (
                              <div key={`${file.name}-${file.lastModified}`}>
                                {file.name}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </Card>
                  ))}
                </Col>
              </Row>
            </Tab>

            {/* 🔒 Tab 2: Security */}
            <Tab
              eventKey="security"
              title={
                <span className="tab-title">
                  <FaLock className="me-2" />
                  {t("driverInfo.tabSecurity")}
                </span>
              }
            >
              <Card
                className="dark-section"
                style={{
                  border: "1px solid #3a3a3a",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                  background:
                    "linear-gradient(145deg, #2a2a2a 0%, #1e1e1e 50%, #252525 100%)",
                }}
              >
                <Card.Body className="p-4">
                  <div className="text-center mb-4">
                    <FaLock
                      size={40}
                      style={{ color: "#ff7b00" }}
                      className="mb-3"
                    />
                    <h5 className="fw-bold mb-3 dark-text-white">
                      {t("driverInfo.changePassword")}
                    </h5>
                    <p className="dark-text-muted mb-4">
                      {t("driverInfo.changePasswordDescription")}
                    </p>
                  </div>

                  <Form onSubmit={handlePasswordSubmit}>
                    <Row className="g-3">
                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold dark-text-white">
                            {t("driverInfo.oldPassword")} *
                          </Form.Label>

                          <div className="password-input-wrapper">
                            <Form.Control
                              type={showPasswords.old_password ? "text" : "password"}
                              name="old_password"
                              value={passwordData.old_password}
                              onChange={handlePasswordChange}
                              isInvalid={!!passwordErrors.old_password}
                              placeholder={t("driverInfo.oldPasswordPlaceholder")}
                              className="dark-bg-dark dark-text-white border-secondary password-input"
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => togglePassword("old_password")}
                              tabIndex={-1}
                              aria-label={
                                showPasswords.old_password
                                  ? t("common.hidePassword", "Hide password")
                                  : t("common.showPassword", "Show password")
                              }
                            >
                              {showPasswords.old_password ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>

                          <Form.Control.Feedback type="invalid">
                            {passwordErrors.old_password}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold dark-text-white">
                            {t("driverInfo.newPassword")} *
                          </Form.Label>

                          <div className="password-input-wrapper">
                            <Form.Control
                              type={showPasswords.new_password ? "text" : "password"}
                              name="new_password"
                              value={passwordData.new_password}
                              onChange={handlePasswordChange}
                              isInvalid={!!passwordErrors.new_password}
                              placeholder={t("driverInfo.newPasswordPlaceholder")}
                              className="dark-bg-dark dark-text-white border-secondary password-input"
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => togglePassword("new_password")}
                              tabIndex={-1}
                              aria-label={
                                showPasswords.new_password
                                  ? t("common.hidePassword", "Hide password")
                                  : t("common.showPassword", "Show password")
                              }
                            >
                              {showPasswords.new_password ? <FaEyeSlash /> : <FaEye />}
                            </button>
                          </div>

                          {passwordData.new_password && (
                            <div className="mt-2">
                              <div className="d-flex justify-content-between mb-1">
                                <small className="text-muted">{t("common.passwordStrength", "Password strength")}</small>
                                <small style={{ color: getPasswordStrength(passwordData.new_password).color, fontWeight: 600 }}>
                                  {getPasswordStrength(passwordData.new_password).label}
                                </small>
                              </div>
                              <div className="password-strength-bar">
                                <div
                                  className="password-strength-fill"
                                  style={{
                                    width: `${(getPasswordStrength(passwordData.new_password).level / 4) * 100}%`,
                                    backgroundColor: getPasswordStrength(passwordData.new_password).color,
                                  }}
                                />
                              </div>
                            </div>
                          )}

                          <Form.Control.Feedback type="invalid">
                            {passwordErrors.new_password}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold dark-text-white">
                            {t("driverInfo.confirmNewPassword")} *
                          </Form.Label>

                          <div className="password-input-wrapper">
                            <Form.Control
                              type={
                                showPasswords.new_password_confirmation ? "text" : "password"
                              }
                              name="new_password_confirmation"
                              value={passwordData.new_password_confirmation}
                              onChange={handlePasswordChange}
                              isInvalid={!!passwordErrors.new_password_confirmation}
                              placeholder={t("driverInfo.confirmPasswordPlaceholder")}
                              className="dark-bg-dark dark-text-white border-secondary password-input"
                            />
                            <button
                              type="button"
                              className="password-toggle-btn"
                              onClick={() => togglePassword("new_password_confirmation")}
                              tabIndex={-1}
                              aria-label={
                                showPasswords.new_password_confirmation
                                  ? t("common.hidePassword", "Hide password")
                                  : t("common.showPassword", "Show password")
                              }
                            >
                              {showPasswords.new_password_confirmation ? (
                                <FaEyeSlash />
                              ) : (
                                <FaEye />
                              )}
                            </button>
                          </div>

                          <Form.Control.Feedback type="invalid">
                            {passwordErrors.new_password_confirmation}
                          </Form.Control.Feedback>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="text-center mt-4">
                      <Button
                        type="submit"
                        disabled={passwordLoading}
                        style={{
                          backgroundColor: "#ff7b00",
                          border: "none",
                          fontWeight: "bold",
                          padding: "10px 35px",
                          borderRadius: "25px",
                        }}
                      >
                        {passwordLoading ? (
                          <>
                            <Spinner as="span" animation="border" size="sm" className="me-2" />
                            {t("common.changing")}
                          </>
                        ) : (
                          <>
                            <FaLock className="me-2" />
                            {t("actions.changePassword")}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>

            {/* 🗑️ Tab 3: Delete Account */}
            <Tab
              eventKey="delete"
              title={
                <span className="tab-title">
                  <FaTrash className="me-2" />
                  {t("driverInfo.tabDeleteAccount")}
                </span>
              }
            >
              <Card
                className="dark-section"
                style={{
                  border: "1px solid #3a3a3a",
                  boxShadow: "0 4px 15px rgba(0,0,0,0.3)",
                  background:
                    "linear-gradient(145deg, #2a2a2a 0%, #1e1e1e 50%, #252525 100%)",
                }}
              >
                <Card.Body className="p-4">
                  <div className="text-center mb-4">
                    <FaTrash size={40} className="text-danger mb-3" />
                    <h5 className="fw-bold mb-3 text-danger">
                      {t("driverInfo.deleteAccount")}
                    </h5>
                    <p className="dark-text-muted mb-4">
                      {t("driverInfo.deleteAccountWarning")}
                    </p>
                  </div>

                  <Form onSubmit={handleDeleteAccount}>
                    <Row className="g-3">
                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold dark-text-white">
                            {t("driverInfo.currentPassword")} *
                          </Form.Label>
                          <Form.Control
                            type="password"
                            name="password"
                            value={deleteData.password}
                            onChange={handleDeleteChange}
                            isInvalid={!!deleteErrors.password}
                            placeholder={t(
                              "driverInfo.currentPasswordPlaceholder",
                            )}
                            className="dark-bg-dark dark-text-white border-secondary"
                          />
                          <Form.Control.Feedback type="invalid">
                            {deleteErrors.password}
                          </Form.Control.Feedback>
                          <Form.Text className="dark-text-muted">
                            {t("driverInfo.passwordRequiredForDelete")}
                          </Form.Text>
                        </Form.Group>
                      </Col>

                      <Col xs={12} md={12}>
                        <Form.Group>
                          <Form.Label className="fw-bold dark-text-white">
                            {t("driverInfo.deleteReason")}
                          </Form.Label>
                          <Form.Control
                            as="textarea"
                            rows={3}
                            name="deleted_reason"
                            value={deleteData.deleted_reason}
                            onChange={handleDeleteChange}
                            placeholder={t(
                              "driverInfo.deleteReasonPlaceholder",
                            )}
                            className="dark-bg-dark dark-text-white border-secondary"
                          />
                          <Form.Text className="dark-text-muted">
                            {t("driverInfo.deleteReasonHelp")}
                          </Form.Text>
                        </Form.Group>
                      </Col>
                    </Row>

                    <div className="text-center mt-4">
                      <Button
                        type="submit"
                        disabled={deleteLoading}
                        style={{
                          backgroundColor: "#dc3545",
                          border: "none",
                          fontWeight: "bold",
                          padding: "10px 35px",
                          borderRadius: "25px",
                        }}
                      >
                        {deleteLoading ? (
                          <>
                            <Spinner
                              as="span"
                              animation="border"
                              size="sm"
                              className="me-2"
                            />
                            {t("common.deleting")}
                          </>
                        ) : (
                          <>
                            <FaTrash className="me-2" />
                            {t("actions.deleteAccount")}
                          </>
                        )}
                      </Button>
                    </div>
                  </Form>
                </Card.Body>
              </Card>
            </Tab>
          </Tabs>
        </Container>
      </Container>

      {(userType === "client" || userType === "driver") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
}
